import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import {
  getElevationProfileExpansionLevel,
  getIsLeftMenuCollapsedSelector,
  getIsPointsMenuModeSelector,
  getIsWaypointSelectedSelector,
  getIsWaypointDetailsOpenSelector,
  getSortedGuideDataSelector,
} from 'store/GuideUse/selectors';
import { WaypointSearchInput } from './WaypointSearchInput/WaypointSearchInput';
import { GuideUseMap } from 'containers/GuideUseMap/GuideUseMap';
import { LoadingOverlay } from 'shared/LoadingOverlay/LoadingOverlay';
import { ScrollSaverMenuWrapper } from 'shared/ScrollSaverMenuWrapper/ScrollSaverMenuWrapper';
import { MapSettingsModal } from './MapSettingsModal/MapSettingsModal';
import { LayersModal } from './LayersModal/LayersModal';
import { GuideUseControlsPanel } from './ControlsPanel/ControlsPanel';
import { PointsMenu } from './PointsMenu/PointsMenu';
import { GuidesOptionsPanel } from './GuidesOptionsPanel/GuidesOptionsPanel';
import { ElevationProfile } from './ElevationProfile/ElevationProfile';
import { PointDetails } from './PointDetails/PointDetails';
import { ElevationProfileModalContainer } from '../../containers/ElevationProfileModal/ElevationProfileModal';
import { GeoRouteBuilder } from 'services/Navigation/GeoRouteBuilder/GeoRouteBuilder';
import { Modal, Button, Input } from "antd";
import { S } from './GuideUse.styles';
import {
  GeoTrail,
  GeoWaypoint,
  GuideUseViewProps,
} from 'containers/GuideUse/GuideUse.interface';
import { ZoomButtonsView } from '../ZoomButtons/ZoomButtons';
import { FloatPlacement } from '../../commonTypes/enums';
import { PlannedRoutesPanel } from './PlannedRoutes/PlannedRoutesPanel/PlannedRoutesPanel';
import { useAuth } from 'services/AuthManager/AuthManager.react';
import { useHistory } from 'react-router-dom';
import { getActivePlannedRouteSelector, getPlannedRoutesSavingStateSelector } from 'store/UserStore/selectors';
import { PlannedRouteMarker, PlannedRouteMarkersData } from 'containers/GuideUseMap/GuideUseMap.interfaces';
import { GuideGeoExt, PlannedRouteExt, PlannedRoutePinInfo } from 'commonTypes/GuideUse/guideGeo';
import { userActions } from 'store/UserStore';
import { actions as guideUseActions } from 'store/GuideUse/actions';
import { PlanningRoutePanel } from './PlannedRoutes/PlanningRoutePanel/PlanningRoutePanel';
import { FormattedMessage, useIntl } from 'react-intl';
import RenameGuideModal from "./PlannedRoutes/RenameGuideModal";
import { toast } from 'react-toastify';

const GuideUseComponent: React.FC<GuideUseViewProps> = ({
  isElevationProfileExpanded,
  guideData,
  isGuideDataFetching,
  waypointsMenuScrollPosition,
  waypoints,
  map,
  handleSetMap,
  handleSetWaypointsMenuScrollPosition,
  onSetIsLeftMenuCollapsed,
  onSetIsLeftMenuExpanded,
  onPointClick,
  onRouteChanged,
  isMobile,
  handleCollapsedElevationProfileZoomIn,
  handleCollapsedElevationProfileZoomOut,
  isElevationProfileModalOpened,
}) => {
  const dispatch = useDispatch();

  const [isPlannedRoutesPanelOpened, setIsPlannedRoutesPanelOpened] = useState<boolean>(false);
  const [isPlannedRoutesPanelTempClosed, setIsPlannedRoutesPanelTempClosed] = useState<boolean>(false);
  const [isPlanningRoutePanelOpened, setIsPlanningRoutePanelOpened] = useState<boolean>(false);
  const isWaypointDetailsOpen = useSelector(getIsWaypointDetailsOpenSelector);
  const isPointsMenuMode = useSelector(getIsPointsMenuModeSelector);
  const isLeftMenuCollapsed = useSelector(getIsLeftMenuCollapsedSelector);
  const elevationProfileExpansionLevel = useSelector(getElevationProfileExpansionLevel);
  const guide:GuideGeoExt = useSelector(getSortedGuideDataSelector);
  const plannedRoute = useSelector(getActivePlannedRouteSelector) as PlannedRouteExt;
  const isPlannedRoutesSaving = useSelector(getPlannedRoutesSavingStateSelector);
  const { isSignedIn } = useAuth();
  const history = useHistory();
  let intl = useIntl()

  ////////////////////////////////////////////////////////////////
  // Planned Routes
  ////

  const handlePlannedRoutesPanelShow = () => {
    if (isPlanningRoutePanelOpened){
      return;
    }
    if (isSignedIn){
      dispatch(guideUseActions.setSelectedWaypoint(''));
      dispatch(guideUseActions.setIsWaypointDetailsOpen(false));
      showPlannedRoutesPanel();
      onSetIsLeftMenuExpanded();
    }
    else{
      history.push('/login');
    }
  };

  const handlePlannedRoutesPanelClose = () => {
    setIsPlannedRoutesPanelOpened(false);
    setIsPlannedRoutesPanelTempClosed(false);
  };

  const handleRouteEditClick = (plannedRoute: PlannedRouteExt) => {
    startEditingPlannedRoute(plannedRoute);
  };

  const showDeleteRouteConfirmation = (selectedPlannedRoute: PlannedRouteExt) => {
    Modal.confirm({
      title: intl.formatMessage({id:"planned_route"}),
      content: intl.formatMessage({id:"planned_route_delete_confirmation"}),
      okText: "Ok",
      cancelText: intl.formatMessage({id:"cancel"}),
      onOk() {
        dispatch(userActions.deletePlannedRoute(selectedPlannedRoute));
      },
      onCancel() {
        console.log("Action canceled.");
      },
    });
  };

  const handleRouteDeleteClick = (selectedPlannedRoute: PlannedRouteExt) => {
      showDeleteRouteConfirmation(selectedPlannedRoute);
  };

  function showPlannedRoutesPanel(){
    setIsPlannedRoutesPanelOpened(true);
    setIsPlannedRoutesPanelTempClosed(false);
  }


  ////
  // Planned Routes
  ////////////////////////////////////////////////////////////////

  ////////////////////////////////////////////////////////////////
  // Planned Route Editor 
  ////

  const [renameModalOpen, setRenameModalOpen] = useState(false);  

  const showEditRouteCancelConfirmation = () => {
    Modal.confirm({
      title: intl.formatMessage({id:"planned_route"}),
      content: intl.formatMessage({id:"planned_route_cancel_msg"}),
      okText: "Ok",
      cancelText: intl.formatMessage({id:"cancel"}),
      onOk() {
        closePlanningRoutePanel(true);
      },
      onCancel() {
        console.log("Action canceled.");
      },
    });
  };

  function closePlanningRoutePanel(isSuccess: boolean = false){
      setIsPlanningRoutePanelOpened(false);
      dispatch(userActions.endEditingPlannedRoute(isSuccess));

      if (isPlannedRoutesPanelTempClosed){
          showPlannedRoutesPanel();
      }
  }

  const startEditingPlannedRoute = (plannedRoute?: PlannedRouteExt) => {
      if (isPlannedRoutesPanelOpened){
          setIsPlannedRoutesPanelTempClosed(true);
      }

      setIsPlanningRoutePanelOpened(true);
      setIsPlannedRoutesPanelOpened(false);
      onSetIsLeftMenuExpanded();
      dispatch(userActions.startEditingPlannedRoute({guide, plannedRoute}));

      toast.info(<FormattedMessage id="planning_route_toast"/>, {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 5000, //false
        draggable: true
      });

  }

  const handleStartPlanningNewRoute = () => {
    if (isSignedIn){
      dispatch(guideUseActions.setSelectedWaypoint(''));
      dispatch(guideUseActions.setIsWaypointDetailsOpen(false));
      startEditingPlannedRoute();
    }
    else{
      history.push('/login');
    }
  };

  const handleEditingPlannedRoutePanelClose = () => {
      if (isPlanningRoutePanelOpened){
          if (plannedRoute != null && plannedRoute.pins.length > 0 && (plannedRoute.isEdited || plannedRoute.isNew)){
            showEditRouteCancelConfirmation();
            return;
          }
          closePlanningRoutePanel();
      }
  };

  const handleAddPlannedRoutePin = (latlng: L.LatLng) => {
      if (plannedRoute){
          let point = guide.routeTree?.getNearestPointThreshold(latlng.lat, latlng.lng, 500);
          if (point != null){
              dispatch(userActions.addEditingPlannedRoutePin({point, guide}));
          }
      }
  };

  const handleRemovePlannedRoutePin = (pinsInfo : PlannedRoutePinInfo) => {
      dispatch(userActions.removeEditingPlannedRoutePin({pinsInfo, guide}));
  };   

  const handlePlannedRouteSave = () => {
      dispatch(userActions.savePlannedRoute(plannedRoute));
  };   

  const handlePlannedRouteRename = () => {
      setRenameModalOpen(true)
  };

  const handlePlannedRouteReverse = () => {
      dispatch(userActions.reverseDirectionPlanningRoute({guide}));
  };

  const handlePlannedRouteChangeDescription = (description: string) => {
      dispatch(userActions.changeDescriptionPlanningRoute({plannedRoute, description}));
  };

  ////
  // Planned Route Editor 
  ////////////////////////////////////////////////////////////////
  
  const recountData = useMemo(() => {
    if (guideData.trails && guideData.waypoints) {
      return {
        trails: guideData.trails.filter(
          (trail: GeoTrail) => trail.geometry && trail.geometry.coordinates
        ),
        waypoints: guideData.waypoints.filter(
          (waypoint: GeoWaypoint) =>
            waypoint.geometry && waypoint.geometry.coordinates
        ),
      };
    }
  }, [guideData.trails, guideData.waypoints]);

  const getWaypointsForPointMenu = useMemo(() => {
    if (waypoints) {
      const extendedWaypoints = waypoints?.map((waypoint: GeoWaypoint) => ({
        ...waypoint,
        guideName: guideData.guideName,
        elevation: waypoint.geometry?.coordinates[2],
      }));

      return [
        ...extendedWaypoints
          .filter(
            waypoint =>
              waypoint.waypointDistance || waypoint.waypointDistance === 0
          )
          .sort((a, b) =>
            (a.waypointDistance as number) < (b.waypointDistance as number)
              ? -1
              : 1
          ),
        ...extendedWaypoints
          .filter(waypoint => waypoint.waypointDistance === null)
          .sort((a, b) => (a.title < b.title ? -1 : 1)),
      ];
    }
  }, [guideData.guideName, waypoints]);

 
  function getPlannedRouteTrailData(guide: GuideGeoExt, route: PlannedRouteExt): PlannedRouteExt | null {
      if (plannedRoute != null && !isGuideDataFetching && guideData != null && guideData.guideId === plannedRoute.guideId) {
          if (plannedRoute.trail == null && route.pins != null){
              const routeBuilder = new GeoRouteBuilder(guide, plannedRoute);
              routeBuilder.setPlannedRoute(route)
              const trail = routeBuilder.buildTrail();

              if (trail != null){
                dispatch(userActions.updatePlannedRouteInfo({plannedRoute, pins:routeBuilder.getPins(), trail}))
              }
          }
      }
      return route
  }

  function createGeoJSONFromPlannedRouteMarkers(route: PlannedRouteExt): PlannedRouteMarkersData {
    const features:PlannedRouteMarker[] = route.pins.map((pinStr: string, index: number) => {
      const [latStr, lngStr] = pinStr.split(',');
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat], // GeoJSON format: [lng, lat]
        },
        properties: {
          id: `${route.id}-${index}`,
        },
      };
    });
    return {
      key: `${route.id}-${route.pins.length}-${route.counter ?? 0}`,
      type: 'FeatureCollection',
      features,
    };
  }  
  
  return (
    <>
      {isGuideDataFetching && <LoadingOverlay />}
      <S.Container>
        {(isPointsMenuMode || isWaypointDetailsOpen || !isMobile) && (
          <S.SideWrapper isMobile={isMobile}>
            <S.Sider
              collapsible
              collapsedWidth={0}
              collapsed={!isMobile && isLeftMenuCollapsed}
              width={isMobile ? '100vw' : 400}
              trigger={null}
            >
              {/* Guide Panel */}
              {!isPlannedRoutesPanelOpened && !isPlanningRoutePanelOpened&& !isGuideDataFetching && (
                <S.ColumnWrapper isMobile={isMobile}>
                  <S.ControlsPanelWrapper isMobile={isMobile}>
                    <GuideUseControlsPanel
                      guideName={guideData.guideName}
                      isMobile={isMobile}
                      isUnlayoutMode={isPointsMenuMode}
                    />
                  </S.ControlsPanelWrapper>
                  {isMobile && (
                    <S.GuidesOptionsPanelWrapper isMobile={isMobile}>
                      <GuidesOptionsPanel
                        hasElevationProfile={!!guideData.primaryDirection}
                        onShowPlannedRoutesPanelClick={handlePlannedRoutesPanelShow}
                        onStartPlanningNewRouteClick={handleStartPlanningNewRoute}
                      />
                    </S.GuidesOptionsPanelWrapper>
                  )}
                  <S.MenuContainer>
                    <S.SearchInputWrapper isMobile={isMobile}>
                      <WaypointSearchInput />
                    </S.SearchInputWrapper>
                    {getWaypointsForPointMenu && (
                      <ScrollSaverMenuWrapper
                        scrollPosition={waypointsMenuScrollPosition}
                        handleSetScrollPosition={
                          handleSetWaypointsMenuScrollPosition
                        }
                      >
                        <PointsMenu
                          waypoints={getWaypointsForPointMenu}
                          primaryDirection={guideData.primaryDirection}
                          onPointClick={onPointClick}
                        />
                      </ScrollSaverMenuWrapper>
                    )}
                  </S.MenuContainer>
                </S.ColumnWrapper>
              )}

              {/* Planned Routes Panel */}
              {isPlannedRoutesPanelOpened && !isWaypointDetailsOpen && !isGuideDataFetching && (
                <S.ColumnWrapper isMobile={isMobile}>
                    <PlannedRoutesPanel
                      isSavingPlannedRoutes={isPlannedRoutesSaving}
                      onRouteChanged={onRouteChanged}
                      onRouteEditClick={handleRouteEditClick}
                      onRouteDeleteClick={handleRouteDeleteClick}
                      onCloseClick={handlePlannedRoutesPanelClose}
                    />
                </S.ColumnWrapper>
              )}

              {/* Route planning Panel */}
              {isPlanningRoutePanelOpened && plannedRoute != null && !isWaypointDetailsOpen && !isGuideDataFetching && (
                <S.ColumnWrapper isMobile={isMobile}>
                    <PlanningRoutePanel
                      plannedRoute={plannedRoute}
                      isSavingPlannedRoutes={isPlannedRoutesSaving}
                      onPinRemoveClick={handleRemovePlannedRoutePin}
                      onSaveClick={handlePlannedRouteSave}
                      onRenameClick={handlePlannedRouteRename}
                      onReverseClick={handlePlannedRouteReverse}
                      onChangeDescription={handlePlannedRouteChangeDescription}
                      onCloseClick={handleEditingPlannedRoutePanelClose}
                    />
                </S.ColumnWrapper>
              )}

            </S.Sider>
            <S.WrapTab onClick={onSetIsLeftMenuCollapsed}>
              {isLeftMenuCollapsed ? <S.RightOutlined /> : <S.LeftOutlined />}
            </S.WrapTab>
            {isWaypointDetailsOpen && (
              <PointDetails/>
            )}
          </S.SideWrapper>
        )}
        {
          <S.GuideBody>
            <GuideUseMap
              map={map}
              handleSetMap={handleSetMap}
              handleAddPlannedRoutePin={handleAddPlannedRoutePin}
              isEditorMode={isPlanningRoutePanelOpened}

              waypointsData={
                !isGuideDataFetching
                  ? {
                      features: guideData.waypoints,
                      type: 'FeatureCollection',
                    }
                  : null
              }

              trailsData={
                !isGuideDataFetching
                  ? {
                      bbox: guideData?.bbox,
                      features: guideData.trails
                        ?.filter(
                          (trail: GeoTrail) =>
                            trail.geometry && trail.geometry.coordinates
                        )
                        ?.map((trail: GeoTrail) => ({
                          ...trail,
                          type: 'Feature',
                          geometry: { ...trail.geometry },
                          coordinates: trail.geometry.coordinates,
                        })),
                      type: 'FeatureCollection',
                    }
                  : null
              }

              plannedRoute={
                guideData != null && !isGuideDataFetching && plannedRoute != null ? getPlannedRouteTrailData(guideData, plannedRoute) : null
              }

              plannedRouteMarkersData={
                plannedRoute != null && !isGuideDataFetching ? createGeoJSONFromPlannedRouteMarkers(plannedRoute) : null
              }
            />

            {(!isMobile || (!isPointsMenuMode && !isWaypointDetailsOpen)) && (
              <S.GuidesOptionsPanelWrapper isMobile={isMobile}>
                <GuidesOptionsPanel
                  hasElevationProfile={!!guideData.primaryDirection}
                  onShowPlannedRoutesPanelClick={handlePlannedRoutesPanelShow}
                  onStartPlanningNewRouteClick={handleStartPlanningNewRoute}
                />
              </S.GuidesOptionsPanelWrapper>
            )}
            {/* @TODO Removed for the first release */}
            {/*{guideData.primaryDirection &&*/}
            {/*!isGuideDataFetching &&*/}
            {/*!isMobile &&*/}
            {/*recountData ? (*/}
            {/*  <S.ElevationProfileWrapper*/}
            {/*    elevationProfileExpansionLevel={elevationProfileExpansionLevel}*/}
            {/*  >*/}
            {/*    <ElevationProfile*/}
            {/*      data={recountData}*/}
            {/*      isMobile={isMobile}*/}
            {/*      isElevationProfileExpanded={isElevationProfileExpanded}*/}
            {/*      handleCollapsedElevationProfileZoomIn={*/}
            {/*        handleCollapsedElevationProfileZoomIn*/}
            {/*      }*/}
            {/*      handleCollapsedElevationProfileZoomOut={*/}
            {/*        handleCollapsedElevationProfileZoomOut*/}
            {/*      }*/}
            {/*    />*/}
            {/*  </S.ElevationProfileWrapper>*/}
            {/*) : (*/}
            {(!isPointsMenuMode || !isMobile) && (
              <ZoomButtonsView
                isAbsolute={true}
                floatButtonsPlacement={FloatPlacement.bottomRight}
                onZoomIn={handleCollapsedElevationProfileZoomIn}
                onZoomOut={handleCollapsedElevationProfileZoomOut}
              />
            )}
            {/*)}*/}
          </S.GuideBody>
        }
      </S.Container>
      {/* @TODO Removed for the first release */}
      {/*{guideData.primaryDirection && !isGuideDataFetching && isMobile && (*/}
      {/*  <ElevationProfileModalContainer*/}
      {/*    visibility={isElevationProfileModalOpened}*/}
      {/*    data={{*/}
      {/*      trails: guideData.trails.filter(*/}
      {/*        (trail: GeoTrail) => trail.geometry && trail.geometry.coordinates*/}
      {/*      ),*/}
      {/*      waypoints: guideData.waypoints.filter(*/}
      {/*        (waypoint: GeoWaypoint) =>*/}
      {/*          waypoint.geometry && waypoint.geometry.coordinates*/}
      {/*      ),*/}
      {/*      iconTypes: guideData.iconTypes,*/}
      {/*    }}*/}
      {/*  />*/}
      {/*)}*/}
      <MapSettingsModal />
      <LayersModal />
      <RenameGuideModal
        open={renameModalOpen}
        currentName={plannedRoute ? (plannedRoute.routeName ? plannedRoute.routeName : "") : ""}
        onCancel={() => setRenameModalOpen(false)}
        onOk={(newName) => {
            setRenameModalOpen(false);
            if (plannedRoute != null){
                dispatch(userActions.renamePlannedRoute({plannedRoute, routeName: newName}));
            }
        }}
      />      
    </>
  );
};

export const GuideUse = memo(GuideUseComponent);
